import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAuthSelector } from '../../services/selectors/authSelector';
import {
    createDriverHandoverRequest,
    fetchDriverHandoverTargets,
    recordDriverActivitySignal,
} from '../../services/apis/dispense/routeAudit';
import { buildHandoverTargetOptions, toTitleCase } from '../../utils/helper/routeAuditUi';
import { HandoverSignatureModal } from './handoverSignature';
import { CloseModelIcon, SendIcon } from '../../assets/svg/icons';

const RECIPIENT_TYPES = [
    { value: 'assistant', label: 'Assistant' },
    { value: 'pharmacist', label: 'Pharmacist' },
    { value: 'instore', label: 'On Duty' },
];

const PatientHandoverSheet = ({ patient, eligible = false, onSent }) => {
    const { pharmacyId } = useAuthSelector();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [shouldLoadTargets, setShouldLoadTargets] = useState(false);
    const [targets, setTargets] = useState({});
    const [recipientType, setRecipientType] = useState('assistant');
    const [selectedTarget, setSelectedTarget] = useState('');
    const [loadingTargets, setLoadingTargets] = useState(false);
    const [showSignature, setShowSignature] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [requestSent, setRequestSent] = useState(false);
    const loadTargetsTimerRef = useRef(null);

    const patientId = Number(patient?.id);
    const medicationRows = patient?.rx_data
        || patient?.patients_win_rx_data
        || patient?.patients_win_rx_data_today_missed
        || patient?.patients_win_rx_data_final_missed
        || [];
    const patientName = toTitleCase([patient?.last_name, patient?.first_name].filter(Boolean).join(' ') || patient?.patient_name || 'Patient');
    const targetGroups = useMemo(() => buildHandoverTargetOptions(targets), [targets]);
    const recipientOptions = recipientType === 'assistant'
        ? targetGroups.teammates
        : recipientType === 'pharmacist'
            ? targetGroups.assistantPharmacists
            : [];
    const requiresRecipientSelection = recipientType !== 'instore';
    const canContinue = !loadingTargets && !saving && (requiresRecipientSelection ? Boolean(selectedTarget) : true);
    const isESignPage = location.pathname === '/e-sign';

    const reset = () => {
        setRecipientType('assistant');
        setSelectedTarget('');
        setShowSignature(false);
        setErrorMessage('');
    };

    const close = () => {
        if (saving) return;
        window.clearTimeout(loadTargetsTimerRef.current);
        setShouldLoadTargets(false);
        setIsOpen(false);
        reset();
    };

    useEffect(() => {
        if (!isOpen || !shouldLoadTargets || !pharmacyId) return undefined;

        let active = true;
        setLoadingTargets(true);
        setErrorMessage('');

        fetchDriverHandoverTargets(pharmacyId)
            .then((response) => {
                if (active) setTargets(response || {});
            })
            .catch(() => {
                if (active) setErrorMessage('Unable to load handover recipients. Please try again.');
            })
            .finally(() => {
                if (active) setLoadingTargets(false);
            });

        return () => {
            active = false;
        };
    }, [isOpen, pharmacyId, shouldLoadTargets]);

    useEffect(() => () => window.clearTimeout(loadTargetsTimerRef.current), []);

    useEffect(() => {
        setRequestSent(false);
    }, [patientId]);

    if (!eligible || !patientId || !Array.isArray(medicationRows) || medicationRows.length === 0) return null;

    const handleRecipientTypeChange = (nextType) => {
        setRecipientType(nextType);
        setSelectedTarget('');
        setErrorMessage('');
    };

    const handleOpen = () => {
        setLoadingTargets(true);
        setErrorMessage('');
        setIsOpen(true);
        setShouldLoadTargets(false);
        window.clearTimeout(loadTargetsTimerRef.current);

        // Keep the animation work separate from parsing the large recipient list.
        loadTargetsTimerRef.current = window.setTimeout(() => setShouldLoadTargets(true), 320);
    };

    const handleOpenSignature = () => {
        if (!canContinue) {
            setErrorMessage('Choose who will receive this patient medication.');
            return;
        }

        setErrorMessage('');
        setShowSignature(true);
    };

    const handleSend = async (signatureBase64) => {
        const [targetType, rawTargetId] = selectedTarget.split(':');
        const isOnDutyHandover = recipientType === 'instore';

        setSaving(true);
        setErrorMessage('');
        try {
            await createDriverHandoverRequest({
                pharmacyId,
                targetType: isOnDutyHandover ? 'pharmacy_user' : targetType,
                targetId: isOnDutyHandover ? null : Number(rawTargetId),
                sendToAllInstore: isOnDutyHandover,
                handoverType: 'patient',
                patientId,
                signatureBase64,
            });
            recordDriverActivitySignal({ pharmacyId, route: `${patient?.route || ''}`.trim() || undefined });
            setShowSignature(false);
            setIsOpen(false);
            reset();
            setRequestSent(true);
            onSent?.();
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.data?.error?.handover?.[0]
                || error?.response?.data?.message
                || 'Unable to send the handover request. Please try again.'
            );
            setShowSignature(false);
        } finally {
            setSaving(false);
        }
    };

    if (requestSent) {
        return <span className="text-xs font-semibold text-amber-700">Handover request sent</span>;
    }

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                title="Hand over medication"
                className={`inline-flex min-h-7 items-center justify-center gap-0.5 rounded-md border border-indigo-200 bg-indigo-50 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-300 active:bg-indigo-200 ${isESignPage ? 'h-7 w-7 px-0' : 'px-1.5'}`}
                style={isESignPage ? undefined : { minWidth: '84px' }}
                aria-label="Hand over medication"
            >
                <SendIcon className="h-2.5 w-2.5" />
                {!isESignPage ? <span>Hand over</span> : null}
            </button>

            {typeof document !== 'undefined' ? createPortal(
                <AnimatePresence>
                    {isOpen ? (
                        <motion.div
                            className="fixed inset-0 z-[1200]"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                        >
                            <button
                                type="button"
                                aria-label="Close handover"
                                onClick={close}
                                className="absolute inset-0 h-full w-full cursor-default bg-slate-950/45"
                            />
                            <motion.section
                                role="dialog"
                                aria-modal="true"
                                aria-label={`Handover medication for ${patientName}`}
                                className="absolute bottom-0 flex h-[72vh] max-h-[72vh] w-full min-h-0 flex-col rounded-t-[28px] bg-white shadow-2xl [height:72dvh] [max-height:72dvh]"
                                initial={{ y: '100%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '100%' }}
                                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <div className="flex justify-center pb-1 pt-3">
                                    <span className="h-1 w-9 rounded-full bg-slate-300" />
                                </div>
                                <div className="flex items-start justify-between border-b border-slate-200 px-5 pb-4 pt-2">
                                <div className="min-w-0 flex-1">
                                    <h3 className="truncate whitespace-nowrap text-base font-bold text-slate-900">Handover medication</h3>
                                    <p className="mt-1 truncate whitespace-nowrap text-sm text-slate-600" title={`For ${patientName}`}>For {patientName}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={close}
                                    className="ml-4 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
                                    aria-label="Close handover"
                                >
                                    <CloseModelIcon />
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                                <div>
                                    <p className="mb-3 text-sm text-slate-600">Choose who will receive this patient medication.</p>
                                    <p className="mb-2 text-sm font-semibold text-slate-800">Send to</p>
                                    <div className="grid grid-cols-3 gap-2">
                                        {RECIPIENT_TYPES.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => handleRecipientTypeChange(option.value)}
                                                className={`rounded-xl border px-2 py-2.5 text-xs font-semibold transition ${recipientType === option.value ? 'border-indigo-200 bg-indigo-50 text-indigo-800' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {requiresRecipientSelection ? (
                                    <label className="block text-sm font-semibold text-slate-800">
                                        {recipientType === 'assistant' ? 'Assistant' : 'Pharmacist'}
                                        <select
                                            value={selectedTarget}
                                            onChange={(event) => setSelectedTarget(event.target.value)}
                                            disabled={loadingTargets}
                                            className="mt-1 h-12 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100 disabled:opacity-60"
                                        >
                                            <option value="">{loadingTargets ? 'Loading recipients...' : `Choose ${recipientType === 'assistant' ? 'an assistant' : 'a pharmacist'}...`}</option>
                                            {recipientOptions.map((target) => <option key={target.value} value={target.value}>{target.label}</option>)}
                                        </select>
                                    </label>
                                ) : (
                                    <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm text-indigo-800">The on-duty pharmacy team will receive this request.</div>
                                )}

                                {errorMessage ? <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMessage}</p> : null}
                            </div>

                            <div className="border-t border-slate-200 px-5 py-4">
                                <button
                                    type="button"
                                    onClick={handleOpenSignature}
                                    disabled={!canContinue}
                                    className="w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Sign & Send Handover Request
                                </button>
                            </div>
                            </motion.section>
                        </motion.div>
                    ) : null}
                </AnimatePresence>,
                document.body
            ) : null}

            <HandoverSignatureModal
                isOpen={showSignature}
                title={`Handover medication for ${patientName}`}
                description="Sign below to send this patient handover request."
                confirmLabel="Send Handover Request"
                confirmButtonClassName="bg-slate-900 hover:bg-slate-800"
                onCancel={() => setShowSignature(false)}
                onConfirm={handleSend}
                loading={saving}
                errorMessage={errorMessage}
            />
        </>
    );
};

export default PatientHandoverSheet;
